import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, DialogNugget, handleAskLubavApiCall } from '@backrs_ui/common-ui';

import { AskLubavError } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavError';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { postContentNuggetCompleted } from '@backrs_ui/grow-ui/src/redux/user/thunks/nuggetCompleted';
import { selectBookmarkedNuggetId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Dialog, SxProps, Theme } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import {
  determineCustomMenuItems,
  determineDescription,
  determineSubtitle,
  determineTitle,
  determineTitleOnClick,
  getShareUrl,
  handleBookmark,
  handlePostHogCapture,
  nuggetMapper,
} from './nugget.util';

const LUBAV_API_URL = process.env.REACT_APP_LUBAV_API_BASEURL;
const BACKRS_URL = process.env.REACT_APP_BACKRS_URL;

type MappedDialogNuggetProps = Readonly<{
  activeDialogNugget?: ContentNugget;
  handleClose: (isOpen: boolean) => void;
}>;

export const MappedDialogNugget = ({
  activeDialogNugget,
  handleClose,
}: MappedDialogNuggetProps) => {
  const dispatch = useAppDispatch();
  const postHog = usePostHog();
  const navigate = useNavigate();

  const completedNuggetIds = useAppSelector((state) => state.user.user?.completed_nugget_ids);
  const user = useAppSelector((state) => state.user.user);
  const mostRecentResponseSetTrack = useAppSelector((state) => state.userResponseSet.results?.tier);
  const mostRecentCareerPathSuggestions = useAppSelector(
    (state) => state.userResponseSet.results?.careerPathSuggestions,
  );
  const mostRecentResponseSetId = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.id,
  );
  const [fitExplainerText, setFitExplainerText] = useState<string | undefined>(undefined);
  const [askLubavButtonResponse, setAskLubavButtonResponse] = useState<string | undefined>(
    undefined,
  );

  const bookmarkId: number | undefined = useAppSelector(
    selectBookmarkedNuggetId({
      nuggetId: activeDialogNugget?.id,
      variant: activeDialogNugget?.variant,
    }),
  );

  const getFit = () => {
    if (activeDialogNugget?.variant !== 'career-path') return undefined;

    return Object.values(mostRecentCareerPathSuggestions || {}).find(
      ({ careerPath }) => careerPath.id === activeDialogNugget.id,
    )?.scoreSection;
  };

  const handleMarkAsDone = () => {
    if (activeDialogNugget) {
      dispatch(postContentNuggetCompleted(activeDialogNugget.id));
    }
  };

  const getFitExplainerClick = async () => {
    if (!LUBAV_API_URL) {
      return;
    }

    if (activeDialogNugget?.variant === 'career-path') {
      setFitExplainerText(''); // Reset the text before starting
      const response = await handleAskLubavApiCall({
        url_key: 'career-track/explain-career-path-fit',
        inputData: {
          user_id: user?.id,
          career_path_id: activeDialogNugget.id,
          response_set_id: mostRecentResponseSetId,
        },
        apiUrl: LUBAV_API_URL,
        userId: user?.id,
        getToken: getBackrsToken,
        askLubavResponseAccumulator: (chunk) => {
          setFitExplainerText((prev) => (prev || '') + chunk);
        },
      });

      return response;
    }

    return undefined;
  };

  const listOfNuggetTypesThatAreNotCompletable = [
    'scholarships',
    'career-path',
    'profile-internal',
    'internship',
  ];

  const hasCompletedNugget =
    activeDialogNugget &&
    !listOfNuggetTypesThatAreNotCompletable.includes(activeDialogNugget.variant) // if the variant is not completable, then we always show false
      ? (completedNuggetIds?.includes(activeDialogNugget.id) ?? false)
      : false;

  const mappedNugget = activeDialogNugget ? (
    <DialogNugget
      // content nugget make up
      title={determineTitle(activeDialogNugget)}
      titleOnClick={determineTitleOnClick(activeDialogNugget)}
      subTitle={determineSubtitle(activeDialogNugget)}
      description={determineDescription(activeDialogNugget)}
      variant={nuggetMapper({
        nugget: activeDialogNugget,
        user,
        mostRecentResponseSetTrack,
        askLubavResponseAccumulator: (chunk) => {
          setAskLubavButtonResponse((prev) => (prev || '') + chunk);
        },
        setAskLubavButtonResponse,
        askLubavButtonResponse,
        AskLubavErrorDisplay: ({ errorCode }) =>
          errorCode && (
            <AskLubavError
              errorCode={errorCode}
              onBackrsProfileClick={() => {
                // TODO: point to Grow profile page once it is built
                window.open(`${BACKRS_URL}/profile/${user?.id}`, '_blank');
              }}
              onBlueprintClick={() => {
                navigate(APP_ROUTE.SURVEY);
              }}
              onNetworksClick={() => {
                window.open(BACKRS_URL, '_blank');
              }}
              onProsperClick={() => {
                navigate(APP_ROUTE.PROSPER);
              }}
            />
          ),
      })}
      columnCategory={
        activeDialogNugget?.content_nugget_theme ||
        activeDialogNugget.scholarship_info?.award_type ||
        ''
      }
      // user actions
      saveToLibrary={() =>
        handleBookmark({
          nuggetId: activeDialogNugget.id,
          variant: activeDialogNugget.variant,
          dispatch,
          bookmarkId,
        })
      }
      customMenuActions={determineCustomMenuItems(activeDialogNugget)}
      shareParams={{
        title: `Prosper: ${activeDialogNugget.title}`,
        text: `\n\nProsper: ${activeDialogNugget.description}`,
        url: getShareUrl({
          contentNugget: activeDialogNugget,
        }),
        afterShare: () => handlePostHogCapture(activeDialogNugget, postHog),
      }}
      markAsDone={!activeDialogNugget.type ? undefined : handleMarkAsDone}
      // user nugget state
      hasBookmarkedNugget={!!bookmarkId}
      hasCompletedNugget={hasCompletedNugget}
      fit={getFit()}
      fitExplainerOnClick={getFitExplainerClick}
      fitExplainerText={fitExplainerText}
      user={user}
    />
  ) : null;

  return (
    <Dialog
      open={!!activeDialogNugget}
      onClose={() => handleClose(false)}
      maxWidth="md"
      sx={dialogStyle}
    >
      {mappedNugget}
    </Dialog>
  );
};

const dialogStyle: SxProps<Theme> = {
  zIndex: 99999,
  overflowY: 'auto',
};
